import React from 'react';
import CopyButton from '@liquidweb-dev/undercurrent/CopyButton';
import Box, { BoxProps } from '@liquidweb-dev/undercurrent/Box';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';

type CodeBlockProps = BoxProps;

/**
 * Custom component for rendering <pre> tags with copy functionality
 */
function CodeBlock(props: CodeBlockProps) {
	const { children, sx, ...rest } = props;

	const getTextContent = (node: React.ReactNode): string => {
		if (typeof node === 'string') {
			return node;
		}

		if (typeof node === 'number') {
			return node.toString();
		}

		if (React.isValidElement(node) && node.props.children) {
			return React.Children.toArray(node.props.children)
				.map((child) => getTextContent(child))
				.join('');
		}

		return '';
	};

	const handleCopy = async (): Promise<void> => {
		try {
			const textContent = React.Children.toArray(children)
				.map((child) => getTextContent(child))
				.join('');
			await navigator.clipboard.writeText(textContent);
		} catch (err) {
			console.error('Failed to copy text: ', err);
		}
	};

	return (
		<Box sx={combineSx({ position: 'relative' }, sx)} {...rest}>
			<CopyButton
				iconButtonProps={{
					variant: 'barebones',
					sx: {
						position: 'absolute',
						top: 8,
						right: 8,
					},
				}}
				onClick={handleCopy}
			/>

			<Typography
				component="pre"
				fontType="monospace"
				variant="label-lg"
				sx={{
					padding: 300,
					background: ({ vars }) => vars.palette.uc.bg.weak,
					display: 'block',
					borderRadius: ({ borderRadius }) => borderRadius.roundedSm,
					overflowX: 'auto',
					'*': {
						// required to override the font-family set by mui css baseline
						fontFamily: 'monospace',
						whiteSpace: 'pre-wrap',
						wordBreak: 'break-word',
					},
				}}
			>
				{children}
			</Typography>
		</Box>
	);
}

export default CodeBlock;
